import React, { useCallback } from 'react'
import { useParams, useSearchParams } from 'react-router-dom'
import { skipToken } from '@reduxjs/toolkit/query'
import { Box, Typography, Paper } from '@mui/material'
import { useTranslation } from 'react-i18next'

import HeartbeatLoader from '@/components/ui/loaders/HeartbeatLoader'

import { useGetNetworkQuery } from '@/core/services/network'
import { useGetChatRoomQuery, useSendChatMessageMutation } from '@/core/services/chat-room'
import { ChatMessages } from '@/features/chat-messages'

export default function NetworkChat() {
  const { t } = useTranslation()
  const { networkId } = useParams()
  const [searchParams] = useSearchParams()
  const chatRoomId = searchParams.get('id')

  const { data: network, isLoading: isLoadingNetwork } = useGetNetworkQuery(networkId ?? skipToken)
  const { data: chatRoom, isLoading: isLoadingChatRoom } = useGetChatRoomQuery(chatRoomId ? { chatRoomId } : skipToken)

  const [sendMessage] = useSendChatMessageMutation()

  const handleSendMessage = useCallback(
    async (message: string) => {
      if (!chatRoomId) return

      await sendMessage({
        chatRoomId,
        message: message,
      }).unwrap()
    },
    [chatRoomId, sendMessage]
  )

  const handleMarkAsRead = useCallback(async (roomId: string) => {}, [])

  const isLoading = isLoadingNetwork || isLoadingChatRoom

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <HeartbeatLoader sx={{ transform: 'scale(2)' }} />
      </Box>
    )
  }

  if (!network || !chatRoom) {
    return (
      <Paper sx={{ p: 3, textAlign: 'center', m: 2 }}>
        <Typography variant="h6" color="text.secondary">
          {t('page.network.chat.notFound', { defaultValue: 'Chat room not found' })}
        </Typography>
      </Paper>
    )
  }

  return (
    <Box sx={{ height: 'calc(100vh - 100px)', display: 'flex', flexDirection: 'column', p: 2 }}>
      <Typography variant="h6">{chatRoom.name}</Typography>

      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', height: '100%' }}>
        <ChatMessages
          messages={chatRoom.messages || []}
          chatRoomId={chatRoomId || undefined}
          onSendMessage={handleSendMessage}
          onMarkAsRead={handleMarkAsRead}
        />
      </Box>
    </Box>
  )
}
